import {
  CATEGORY_LABEL,
  CATEGORY_STYLE,
  FINDING_CATEGORIES,
  findingQuadrant,
  type FindingCategory,
  type FindingWithContext,
} from '../../lib/ctoReviews';

const CELLS: {
  id: 'focus' | 'schedule' | 'fill' | 'skip';
  title: string;
  hint: string;
  focus?: boolean;
}[] = [
  {
    id: 'focus',
    title: 'Do these',
    hint: 'Low effort, high impact',
    focus: true,
  },
  {
    id: 'schedule',
    title: 'Plan these',
    hint: 'High effort, high impact',
  },
  {
    id: 'fill',
    title: 'If leftover',
    hint: 'Low effort, low impact',
  },
  {
    id: 'skip',
    title: 'Leave them',
    hint: 'High effort, low impact',
  },
];

export function CtoFindingMatrix({ findings }: { findings: FindingWithContext[] }) {
  const open = findings.filter((f) => f.status === 'open');
  const byCell = {
    focus: open.filter((f) => findingQuadrant(f) === 'focus'),
    schedule: open.filter((f) => findingQuadrant(f) === 'schedule'),
    fill: open.filter((f) => findingQuadrant(f) === 'fill'),
    skip: open.filter((f) => findingQuadrant(f) === 'skip'),
  };
  const unscored = open.filter((f) => findingQuadrant(f) === 'unscored');
  const maxCell = Math.max(1, ...CELLS.map((cell) => byCell[cell.id].length));

  const byCategory = FINDING_CATEGORIES.map((category) => ({
    category,
    count: findings.filter((f) => f.category === category).length,
  }));
  const uncategorized = findings.filter((f) => !f.category).length;
  const maxCategory = Math.max(1, ...byCategory.map((row) => row.count), uncategorized);

  if (findings.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 p-10 text-center">
        No observations match those filters.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex items-end justify-between gap-3 mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              Effort × impact
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Open tickets. Low effort is trivial or hours. High impact is 4 or 5.
            </p>
          </div>
          <p className="text-xs text-gray-400 tabular-nums">{open.length} open</p>
        </div>

        <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-2 items-stretch">
          <p className="row-span-2 self-center text-[11px] uppercase tracking-wide text-gray-400 [writing-mode:vertical-rl] rotate-180">
            Impact
          </p>
          {CELLS.map((cell) => {
            const rows = byCell[cell.id];
            const share = rows.length / maxCell;
            return (
              <div
                key={cell.id}
                className={`relative overflow-hidden rounded-xl border p-4 min-h-[8.5rem] ${
                  cell.focus
                    ? 'border-fuchsia-300 dark:border-fuchsia-800'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div
                  className={`absolute inset-x-0 bottom-0 ${
                    cell.focus ? 'bg-fuchsia-100 dark:bg-fuchsia-950/70' : 'bg-gray-100 dark:bg-gray-700/50'
                  }`}
                  style={{ height: `${Math.max(rows.length > 0 ? 18 : 0, share * 100)}%` }}
                />
                <div className="relative">
                  <p
                    className={`text-xs font-semibold ${
                      cell.focus
                        ? 'text-fuchsia-800 dark:text-fuchsia-200'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {cell.title}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{cell.hint}</p>
                  <p className="text-3xl font-semibold tabular-nums text-gray-900 dark:text-white mt-3">
                    {rows.length}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        <div className="grid grid-cols-[auto_minmax(0,1fr)_minmax(0,1fr)] gap-2 mt-2">
          <span />
          <p className="text-center text-[11px] uppercase tracking-wide text-gray-400">
            Low effort
          </p>
          <p className="text-center text-[11px] uppercase tracking-wide text-gray-400">
            High effort
          </p>
        </div>
        {unscored.length > 0 ? (
          <p className="text-xs text-gray-400 mt-3">
            {unscored.length} open {unscored.length === 1 ? 'item is' : 'items are'} missing a
            score.
          </p>
        ) : null}
      </section>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
          By area
        </h3>
        <ul className="space-y-2.5">
          {byCategory.map((row) => (
            <CategoryBar
              key={row.category}
              category={row.category}
              count={row.count}
              max={maxCategory}
            />
          ))}
          {uncategorized > 0 ? (
            <li className="flex items-center gap-3">
              <span className="w-36 shrink-0 text-sm text-gray-600 dark:text-gray-300">
                Uncategorized
              </span>
              <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div
                  className="h-full rounded-full bg-gray-400"
                  style={{ width: `${(uncategorized / maxCategory) * 100}%` }}
                />
              </div>
              <span className="w-8 text-right text-sm tabular-nums text-gray-500">
                {uncategorized}
              </span>
            </li>
          ) : null}
        </ul>
      </section>
    </div>
  );
}

function CategoryBar({
  category,
  count,
  max,
}: {
  category: FindingCategory;
  count: number;
  max: number;
}) {
  return (
    <li className="flex items-center gap-3">
      <span className="w-36 shrink-0 text-sm text-gray-700 dark:text-gray-200">
        {CATEGORY_LABEL[category]}
      </span>
      <div className="flex-1 h-3 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
        <div
          className={`h-full rounded-full ${CATEGORY_STYLE[category]}`}
          style={{ width: `${count === 0 ? 0 : Math.max(6, (count / max) * 100)}%` }}
        />
      </div>
      <span className="w-8 text-right text-sm tabular-nums text-gray-500">{count}</span>
    </li>
  );
}
